import { Alert, AlertDescription, AlertTitle, Button, Skeleton, cn } from "@ndd/ui";
import { Loader2 } from "lucide-react";
import type { ReactElement, ReactNode } from "react";
// The explicit React import keeps this module renderable by the node:test
// scenario suite, which compiles JSX to the classic runtime.
import React from "react";
// Relative imports keep this module loadable by the node:test scenario suite,
// which cannot resolve the Vite "@/" alias.
import { SoapDraftRenderer } from "../soap-draft-renderer";
import type {
  AppointmentDraftGenerationUi,
  AppointmentEnhanceActionState,
} from "../../lib/appointment-documentation-state";

/**
 * The Enhanced SOAP draft tab before a persisted Draft exists. `preparing`
 * shows the pink "Running" or "Writing" banner (NO-772) over a placeholder, and may show
 * streamed text, but renders it locked: no editor, no buttons, no copy
 * affordance, and no text selection. `failed` and `cancelled` are distinct,
 * retryable, and never show partial output. `draft_ready` is not this
 * component's: the workspace mounts the editable review surface instead.
 */
export function AppointmentDraftSection({
  generation,
  enhanceAction,
  onRetry,
}: {
  generation: AppointmentDraftGenerationUi;
  enhanceAction: AppointmentEnhanceActionState;
  onRetry: () => void;
}): ReactElement | null {
  if (generation.phase === "idle" || generation.phase === "draft_ready") return null;

  if (generation.phase === "preparing")
    return <PreparingDraft generation={generation} />;

  if (generation.phase === "failed") {
    return (
      <DraftCard>
        <Alert variant="danger" className="m-4">
          <AlertTitle>Enhance didn&apos;t finish.</AlertTitle>
          <AlertDescription className="mt-1">
            The partial output was discarded. Nothing was saved to the draft.
            <div className="mt-2">
              <RetryControl enhanceAction={enhanceAction} onRetry={onRetry} />
            </div>
          </AlertDescription>
        </Alert>
      </DraftCard>
    );
  }

  return (
    <DraftCard>
      <Alert variant="warning" className="m-4">
        <AlertTitle>Enhance was cancelled.</AlertTitle>
        <AlertDescription className="mt-1">
          The partial output was discarded. Nothing was saved to the draft.
          <div className="mt-2">
            <RetryControl enhanceAction={enhanceAction} onRetry={onRetry} />
          </div>
        </AlertDescription>
      </Alert>
    </DraftCard>
  );
}

/** NO-772: "Running", then "Writing" once model output begins; always locked. */
function PreparingDraft({
  generation,
}: {
  generation: Extract<AppointmentDraftGenerationUi, { phase: "preparing" }>;
}): ReactElement {
  return (
    <DraftCard>
      <div
        role="status"
        className="flex shrink-0 items-start gap-2.5 border-b border-ai/20 bg-ai-soft px-4 py-3 text-body leading-5 text-ai"
      >
        <Loader2 aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 animate-spin" />
        <span className="font-medium">
          {generation.writing ? "Writing" : "Running"}
        </span>
      </div>
      {generation.streamedDraftText !== "" ? (
        <LockedDraftPreview markdown={generation.streamedDraftText} />
      ) : (
        <DraftPlaceholder />
      )}
    </DraftCard>
  );
}

/**
 * The streamed draft, shown locked: no editor, no copy affordance, and no
 * text selection. NO-772: the review surface also shows it while the saved
 * Draft loads.
 */
export function LockedDraftPreview({ markdown }: { markdown: string }): ReactElement {
  return (
    <div
      aria-label="Draft preview, locked while enhancing"
      className="min-h-0 flex-1 select-none overflow-y-auto px-5 py-4"
    >
      <SoapDraftRenderer markdown={markdown} />
    </div>
  );
}

/** The editor-shaped card every draft state lives in, so the tab never jumps. */
export function DraftCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <section
      aria-label="SOAP Draft"
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-card shadow-[0_1px_2px_rgba(40,3,15,0.04)]",
        className
      )}
    >
      {children}
    </section>
  );
}

/** Section-heading skeleton while the draft has not started arriving. */
export function DraftPlaceholder({ label }: { label?: string }): ReactElement {
  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4"
      aria-hidden={label ? undefined : true}
    >
      {label ? (
        <p role="status" className="text-body text-muted-foreground">
          {label}
        </p>
      ) : null}
      <Skeleton className="h-3.5 w-40" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-11/12" />
      <Skeleton className="mt-3 h-3.5 w-28" />
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-4/5" />
      <Skeleton className="mt-3 h-3.5 w-36" />
      <Skeleton className="h-3 w-full" />
    </div>
  );
}

function RetryControl({
  enhanceAction,
  onRetry,
}: {
  enhanceAction: AppointmentEnhanceActionState;
  onRetry: () => void;
}): ReactElement | null {
  if (enhanceAction === "unavailable") {
    return <span>Enhance isn&apos;t available right now.</span>;
  }

  if (enhanceAction === "hidden") return null;

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={enhanceAction === "requesting"}
      onClick={onRetry}
    >
      {enhanceAction === "requesting" ? "Starting…" : "Try again"}
    </Button>
  );
}
